import React, { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import _debounce from 'lodash-es/debounce';

import { Button, CrossIcon } from '@motorway/mw-highway-code';

import { GA_TAGS, getCategoryForSticky, STICKY_VRM_EVENTS } from 'Utilities/analytics';
import { safeSessionStorage } from 'Utilities/storages';
import T from 'Utilities/texts';

import VrmChecker from 'Components/vrm/VrmChecker/VrmChecker';
import { Content } from 'LegacySBCRA/Content/Content';

import { useBreakpoint } from '../Breakpoints/Breakpoints';

import styles from './StickyVRM.scss';

const CLOSED_STICKY_VRM_KEY = 'disableStickyVRM';

export type StickyVRMProps = Partial<{
  $content: Element;
  analyticsEvents: {
    onButtonClick?: (e: MouseEvent) => void;
    onVrmSubmit?: (e: MouseEvent) => void;
  };
  desktopCTA: ReactNode;
  disabled: boolean;
  header: ReactNode;
  mobileCTA: ReactNode;
  mobileOnly: boolean;
  onSubmit: (e: MouseEvent) => void;
}>;

type StickyState = {
  focusLock?: boolean;
  forceHide?: boolean;
  showStickyVRM?: boolean;
  stickyVRMReady?: boolean;
};

type StickyDimensions = {
  height: number;
  top: number;
  topAbsolute: number;
} | null;

const StickyVRM: React.FC<StickyVRMProps> = ({
  $content,
  analyticsEvents,
  desktopCTA = T.translate('stickyVRMView.desktopCTA'),
  disabled = false,
  header = T.translate('stickyVRMView.strap'),
  mobileCTA = T.translate('stickyVRMView.mobileCTA'),
  mobileOnly = false,
  onSubmit = undefined,
}) => {
  const stickyDimensions = useRef<StickyDimensions>(null);
  const resizeObserver = useRef<ResizeObserver>();
  const [stickyState, setStickyState] = useState<StickyState>({});
  const [closed, setClosed] = useState<boolean>(safeSessionStorage.getItem(CLOSED_STICKY_VRM_KEY, false));

  const breakpoints = useBreakpoint();
  const tabletBreakpoint = breakpoints.maxWidth.breakpointTablet;

  const calculateDimensions = useRef(() => {
    requestAnimationFrame(() => { // https://github.com/airbnb/visx/pull/335
      if (!$content?.getBoundingClientRect) {
        return;
      }

      const dimensions = $content.getBoundingClientRect();

      stickyDimensions.current = Object.assign(dimensions, {
        topAbsolute: window.scrollY + dimensions.top,
      });
    });
  });

  const { pathname } = useLocation();
  const category = getCategoryForSticky(pathname);

  useEffect(() => {
    const show = stickyState?.showStickyVRM;
    if (show) {
      STICKY_VRM_EVENTS.STICKY_VRM_BANNER_LOADED({
        category,
        label: 'old',
      });
    }
  }, [stickyState?.showStickyVRM, category, pathname]);

  useEffect(() => {
    const forceHide = (mobileOnly && !tabletBreakpoint);

    if (!forceHide) {
      calculateDimensions.current();
    }

    setStickyState((state) => ({
      ...state,
      forceHide,
    }));
  }, [tabletBreakpoint, mobileOnly]);

  useEffect(() => {
    const scrollEvent = () => {
      if (!stickyDimensions.current) {
        return;
      } // https://app.clubhouse.io/motorway/story/20032/typeerror-cannot-read-property-topabsolute-of-undefined

      const y = window.scrollY;
      const { height, topAbsolute } = stickyDimensions.current;

      const visible = !!(
        topAbsolute && height && y > topAbsolute && y < (topAbsolute + height - window.innerHeight)
      );

      setStickyState((state) => {
        if (state.forceHide) {
          return state;
        }

        if (state.showStickyVRM !== visible) {
          return {
            ...state,
            // Prevent FOUC
            showStickyVRM: visible,
            stickyVRMReady: true,
          };
        }

        // Returning state will prevent re-renders
        // https://reactjs.org/docs/hooks-reference.html#bailing-out-of-a-state-update
        return state;
      });
    };

    window.addEventListener('scroll', scrollEvent, {
      passive: true,
    } as AddEventListenerOptions);

    if ('ResizeObserver' in window && $content) {
      // Allow blog images etc to load in and window resize
      // https://github.com/pelotoncycle/resize-observer/issues/23
      const resized = _debounce(calculateDimensions.current, 500);

      const resizeOb = new ResizeObserver(resized);
      resizeOb.observe($content);
      resizeObserver.current = resizeOb;
    }

    return () => {
      window.removeEventListener('scroll', scrollEvent, {
        passive: true,
      } as AddEventListenerOptions);

      if ($content) {
        resizeObserver?.current?.unobserve($content);
      }
    };
  }, [$content]);

  if (!$content) {
    return null;
  }

  const onBlur = () => setStickyState((state) => ({
    ...state,
    focusLock: false,
  }));

  const onFocus = () => {
    setStickyState((state) => ({
      ...state,
      focusLock: true,
    }));
    GA_TAGS.STICKY_VRM_INPUT_FOCUSED();
  };

  const onCloseClick = () => {
    setClosed(true);

    resizeObserver?.current?.unobserve($content);
    safeSessionStorage.setItem(CLOSED_STICKY_VRM_KEY, true);
  };

  const handleVrmSubmit = onSubmit ? (...props: Parameters<typeof onSubmit>) => {
    onSubmit(...props);
    onBlur();
  } : undefined;

  const {
    focusLock,
    forceHide,
    showStickyVRM,
    stickyVRMReady,
  } = stickyState;

  const isClosed = closed && !tabletBreakpoint && !mobileOnly;
  const isShown = !forceHide && showStickyVRM && !isClosed;
  const toShow = focusLock || isShown;

  const wrapper = {
    className: cx(styles.stickyVRM, {
      [styles.show]: toShow,
      [styles.ready]: stickyVRMReady,
    }),
  };

  const buttonProps = {
    className: styles.stickyVrmButton,
    labelChildren: (
      <>
        <span className={styles.mobileCTA}>{mobileCTA}</span>
        <span className={styles.desktopCTA}>{desktopCTA}</span>
      </>
    ),
  };

  const events = {
    ...analyticsEvents,
    onButtonClick: (e: React.MouseEvent<Element, globalThis.MouseEvent>) => {
      analyticsEvents?.onButtonClick?.(e);
    },
  };

  return (
    <Content className={styles.stickyVRMContent} {...{ onBlur, onFocus, wrapper }}>
      <Button
        data-thc-button="close"
        icon={CrossIcon}
        label={T.translate('stickyVRMView.close')}
        onClick={onCloseClick}
        showLabel={false}
        type="button"
        variant="tertiary"
      />
      <Link
        onClick={() => STICKY_VRM_EVENTS.STICKY_VRM_BANNER_LOGO_CLICK({
          category,
          label: 'old',
        })}
        to="/"
      >
        <div className={styles.stickyLogo} />
      </Link>
      <div className={styles.stickyStrap}>{header}</div>
      <VrmChecker
        {...{ analyticsEvents: events, buttonProps, handleVrmSubmit }}
        chevron={!tabletBreakpoint}
        className={styles.stickyForm}
        disabled={disabled || !toShow}
        e2eName="sticky vrm checker"
        hideIntent={tabletBreakpoint}
        id={`${!tabletBreakpoint ? 'bottom' : 'top'}-sticky`}
        inputClassName={cx({
          [styles.minTabletWidth]: tabletBreakpoint,
          [styles.bottomForm]: !tabletBreakpoint,
        })}
        large={false}
        snowplowEvents={ true }
      />
    </Content>
  );
};

export default StickyVRM;
